import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import GuideCapture from "@/components/GuideCapture";

const LOUNGE_IMG =
  "https://images.unsplash.com/photo-1601993957728-1e56ab70c5a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxjYWxpZm9ybmlhJTIwY29hc3QlMjBmb2clMjByZWR3b29kJTIwZm9yZXN0JTIwc2VyZW5lJTIwbW9ybmluZ3xlbnwwfHx8fDE3ODg4MTI0MjV8MA&ixlib=rb-4.1.0&q=85";

const struggles = [
  "Constant conflict or communication breakdown",
  "Emotional distance or feeling like roommates",
  "Infidelity and rebuilding trust",
  "Resentment and unresolved hurt",
  "Different parenting styles",
  "Intimacy and connection concerns",
  "Major life transitions",
  "Considering separation or trying to decide what comes next",
];

const CouplesTherapy = () => {
  usePageMeta(
    "Couples Therapy — $250/Session | Holistic Healing Psychotherapy, Inc.",
    "Couples therapy in California for conflict, emotional distance, infidelity, resentment and life transitions. Structured, compassionate, evidence-informed work with a California LCSW."
  );

  return (
    <div data-testid="couples-therapy-page">
      <section className="bg-cream-50 pt-14 pb-16 lg:pt-24 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeIn>
              <p className="eyebrow mb-6" data-testid="couples-eyebrow">Couples Therapy · Primary Specialty</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink" data-testid="couples-headline">
                When you love each other but can't seem to find your way{" "}
                <em className="text-mauve-500">back to each other</em>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-xl" data-testid="couples-subcopy">
                A structured, compassionate environment where both partners can feel heard — while learning to
                understand the patterns underneath the conflict.
              </p>
              <Link
                to="/contact"
                data-testid="couples-schedule-cta"
                className="inline-flex items-center gap-2 mt-9 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm tracking-wide px-7 py-4 transition-all duration-300 hover:bg-forest-700 hover:shadow-[0_12px_32px_rgba(18,55,42,0.3)]"
              >
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
            </FadeIn>
          </div>
          <FadeIn delay={0.15} className="lg:col-span-5 editorial-frame">
            <img
              src={LOUNGE_IMG}
              alt="A serene, light-washed space for reflection"
              className="relative z-10 w-full h-[340px] sm:h-[460px] object-cover"
              data-testid="couples-hero-image"
            />
          </FadeIn>
        </div>
      </section>

      <section className="relative h-[340px] sm:h-[440px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1494774157365-9e04c6720e47?q=80&w=1600&auto=format&fit=crop"
          alt="A couple together in warm evening light"
          className="w-full h-full object-cover"
          data-testid="couples-band-image"
        />
        <div className="absolute inset-0 bg-forest-950/50" />
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <FadeIn>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream-50 text-center tracking-tight" data-testid="couples-band-quote">
              Reconnect. <em className="text-gold-400">Rebuild.</em> Rediscover.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-forest-900 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading dark eyebrow="Sound Familiar?" title="The struggles couples actually bring to therapy." />
          <div className="mt-12 grid sm:grid-cols-2 gap-x-14">
            {struggles.map((s, i) => (
              <FadeIn key={s} delay={i * 0.06}>
                <div className="flex gap-5 py-5 border-t border-cream-50/10" data-testid={`couples-struggle-${i + 1}`}>
                  <span className="mt-2 w-2.5 h-2.5 rotate-45 bg-gold-500/70 shrink-0" aria-hidden="true" />
                  <p className="text-base text-cream-50/80 leading-relaxed">{s}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.15}>
            <p className="mt-10 text-sm text-cream-50/55" data-testid="couples-separation-link-wrap">
              Facing separation or the end of a marriage?{" "}
              <Link to="/separation-counseling" className="text-gold-400 hover:text-gold-300 underline underline-offset-4" data-testid="couples-separation-link">
                Explore separation & post-marital counseling
              </Link>
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-100 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-12">
          <FadeIn>
            <blockquote className="border-l-2 border-gold-500/50 pl-6 max-w-2xl" data-testid="couples-gottman-quote-2">
              <p className="font-serif text-xl sm:text-2xl text-forest-800/90 leading-snug">
                "All anger is composed of two things: a goal, and something blocking that goal."
              </p>
              <cite className="not-italic block mt-3 text-xs uppercase tracking-[0.25em] text-gold-600">— John Gottman</cite>
            </blockquote>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="What to Expect" title="Both partners heard. The pattern understood." />
          <FadeIn delay={0.1}>
            <p className="text-base leading-relaxed text-ink/80 mt-2" data-testid="couples-expect-copy">
              Couples therapy is not about deciding who is right and who is wrong. In our work, you can expect a
              structured, compassionate environment where both partners feel heard while we slow the cycle down,
              understand what keeps it going, and begin practicing something different — in the room and at home. My
              work draws on research-backed approaches including the Gottman Method and Emotionally Focused Therapy
              (EFT), always tailored to the two people in the room.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="mt-12 rounded-2xl border border-gold-500/35 bg-cream-100 p-8 sm:p-10" data-testid="couples-investment-card">
              <p className="eyebrow mb-4">Investment</p>
              <p className="font-serif text-4xl sm:text-5xl text-forest-800" data-testid="couples-fee">$250+ <span className="text-xl text-sage font-sans">per 50-minute couples session</span></p>
              <p className="mt-3 font-serif text-2xl text-forest-800" data-testid="couples-intake-fee">$375 <span className="text-base text-sage font-sans">recommended 90-minute couples intake</span></p>
              <p className="mt-4 text-sm text-sage leading-relaxed max-w-xl">
                Sessions are held virtually. In-office appointments are available Saturdays only, and in-office rates
                may vary. A 48-hour cancellation policy applies (late-cancel fee $150) — with flexibility when the
                unexpected happens.
              </p>
              <Link
                to="/contact"
                data-testid="couples-investment-cta"
                className="inline-flex items-center gap-2 mt-7 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-3.5 transition-colors duration-300 hover:bg-forest-700"
              >
                Schedule a Consultation <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-6 rounded-2xl bg-forest-900 p-8 sm:p-10" data-testid="couples-intensive-card">
              <p className="eyebrow mb-4">Premium Offering</p>
              <p className="font-serif text-3xl sm:text-4xl text-cream-50" data-testid="couples-intensive-title">
                Couples Therapy Intensive
              </p>
              <p className="mt-2 font-serif text-2xl text-gold-400" data-testid="couples-intensive-fee">
                $1,500–$2,000 <span className="text-base text-cream-50/60 font-sans">· 5 focused hours</span>
              </p>
              <p className="mt-4 text-sm text-cream-50/70 leading-relaxed max-w-xl">
                For couples who want meaningful movement in a single day — a structured deep-dive that compresses
                weeks of work into one focused experience. Ideal when weekly sessions feel too slow for where you
                are, or when the relationship can't wait.
              </p>
              <Link
                to="/couples-therapy-intensive"
                data-testid="couples-intensive-link"
                className="inline-flex items-center gap-2 mt-6 rounded-full border border-gold-500/50 text-gold-300 font-semibold text-sm px-6 py-3 transition-colors duration-300 hover:bg-gold-500/10 hover:text-gold-200"
              >
                Explore the Intensive <ArrowRight size={15} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 pb-20 lg:pb-28" data-testid="guide-capture-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <GuideCapture />
          </FadeIn>
        </div>
      </section>

      <CTASection
        title="Your relationship is worth it."
        copy="A brief consultation helps us decide whether this work is the right fit for where you are."
      />
    </div>
  );
};

export default CouplesTherapy;
